// src/services/copyTradingService.js
import { API_BASE_URL } from '../utils/constants';

const getAuthHeaders = () => ({ 'Content-Type': 'application/json' });

const handleResponse = async (response) => {
  if (!response.ok) {
    if (response.status === 401) window.location.href = '/';
    const error = await response.json();
    throw new Error(error.error || error.detail || 'Request failed');
  }
  return response.json();
};

const BASE = `${API_BASE_URL}/copytrading`;

export const copyTradingService = {

  // ── Leader profile ──────────────────────────────────────────────────────────

  async getMyProfile() {
    const res = await fetch(`${BASE}/profile/`, {
      method: 'GET', headers: getAuthHeaders(), credentials: 'include',
    });
    return handleResponse(res);
  },

  async updateProfile(data) {
    const res = await fetch(`${BASE}/profile/`, {
      method: 'PATCH', headers: getAuthHeaders(), credentials: 'include',
      body: JSON.stringify(data),
    });
    return handleResponse(res);
  },

  async regenerateCode() {
    const res = await fetch(`${BASE}/profile/regenerate-code/`, {
      method: 'POST', headers: getAuthHeaders(), credentials: 'include',
    });
    return handleResponse(res);
  },

  async getMyFollowers() {
    const res = await fetch(`${BASE}/followers/`, {
      method: 'GET', headers: getAuthHeaders(), credentials: 'include',
    });
    return handleResponse(res);
  },

  // ── Eligible followers count ────────────────────────────────────────────────
  // Called live from TradingModal as the leader types the copy trade amount.
  // amount: number in the leader's display currency (USD or NGN)
  // stockId: stock.id being traded
  // Returns { eligible, total, amount_display }
  async getEligibleFollowers(amount, stockId) {
    if (!amount || amount <= 0 || !stockId) return null;
    try {
      const params = new URLSearchParams();
      params.append('amount', amount);
      params.append('stock_id', stockId);
      const res = await fetch(`${BASE}/followers/eligible/?${params.toString()}`, {
        method: 'GET', headers: getAuthHeaders(), credentials: 'include',
      });
      return handleResponse(res);
    } catch {
      return null;  // Non-critical — don't break TradingModal if this fails
    }
  },

  async getLeaderCopyTrades() {
    const res = await fetch(`${BASE}/leader/trades/`, {
      method: 'GET', headers: getAuthHeaders(), credentials: 'include',
    });
    return handleResponse(res);
  },

  // ── Copier actions ──────────────────────────────────────────────────────────

  async getMyCopyStatus() {
    const res = await fetch(`${BASE}/status/`, {
      method: 'GET', headers: getAuthHeaders(), credentials: 'include',
    });
    return handleResponse(res);
  },

  async previewCode(code) {
    const res = await fetch(`${BASE}/preview/${code.toUpperCase()}/`, {
      method: 'GET', headers: getAuthHeaders(), credentials: 'include',
    });
    return handleResponse(res);
  },

  async followLeader(code) {
    const res = await fetch(`${BASE}/follow/`, {
      method: 'POST', headers: getAuthHeaders(), credentials: 'include',
      body: JSON.stringify({ code }),
    });
    return handleResponse(res);
  },

  async stopCopying() {
    const res = await fetch(`${BASE}/stop/`, {
      method: 'POST', headers: getAuthHeaders(), credentials: 'include',
    });
    return handleResponse(res);
  },

  async getMyCopyTrades() {
    const res = await fetch(`${BASE}/trades/`, {
      method: 'GET', headers: getAuthHeaders(), credentials: 'include',
    });
    return handleResponse(res);
  },

  // ── Notifications ───────────────────────────────────────────────────────────

  async getNotifications(unreadOnly = false) {
    const url = unreadOnly
      ? `${BASE}/notifications/?is_read=false`
      : `${BASE}/notifications/`;
    const res = await fetch(url, {
      method: 'GET', headers: getAuthHeaders(), credentials: 'include',
    });
    return handleResponse(res);
  },

  async markNotificationsRead({ ids, markAll } = {}) {
    const body = markAll ? { mark_all: true } : { ids };
    const res = await fetch(`${BASE}/notifications/`, {
      method: 'PATCH', headers: getAuthHeaders(), credentials: 'include',
      body: JSON.stringify(body),
    });
    return handleResponse(res);
  },
};